/**
 * User Groups API — all backend calls for the User Groups tab.
 *
 * User groups use the standard mytransaction endpoint (apiTransaction).
 * Cloud campuses use TransType suffix "2" (e.g. group_add2).
 * Legacy campuses use plain TransTypes (e.g. group_add).
 */

import { apiFetch, apiTransaction } from '../../../services/api.js';
import { getCampusConfig } from '../../../services/session.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns the cloud-aware TransType (appends "2" for cloud campuses) */
function cloudType(baseType) {
  const isCloud = !!getCampusConfig()?.is_cloud;
  return isCloud ? `${baseType}2` : baseType;
}

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all user groups for a campus (with their user members).
 *
 * Cloud procedure:  getusersandgroups
 * Legacy procedure: sp_dev_getgroupsandusersbycampusid
 */
export function fetchUserGroups(campusId, readOnly = true) {
  const isCloud = !!getCampusConfig()?.is_cloud;
  return apiFetch({
    procedure: isCloud ? 'getusersandgroups' : 'sp_dev_getgroupsandusersbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Add a new user group.
 *
 * V1 TransType: group_add / group_add2
 *
 * @param {object} data — { group_name, capcode, user_ids, cognito_ids }
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function addUserGroup(data, session) {
  const payload = [{
    campus_id: session.campusId,
    group_name: data.group_name,
    capcode: data.capcode || '',
    user_ids: data.user_ids || [],
    cognito_ids: data.cognito_ids || [],
  }];

  return apiTransaction({
    transType: cloudType('group_add'),
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Edit an existing user group.
 *
 * V1 TransType: group_edit / group_edit2
 *
 * @param {object} data — { group_id, group_name, capcode, user_ids, cognito_ids, remove_list }
 * @param {object} oldData — { group_id, group_name, capcode, user_ids, campus_id }
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function editUserGroup(data, oldData, session) {
  const payload = [{
    group_name: data.group_name,
    group_id: parseInt(data.group_id, 10),
    capcode: data.capcode || '',
    user_ids: data.user_ids || [],
    cognito_ids: data.cognito_ids || [],
    campus_id: session.campusId,
    remove_list: data.remove_list || [],
  }];

  const oldPayload = [{
    group_name: oldData.group_name,
    group_id: parseInt(oldData.group_id, 10),
    capcode: oldData.capcode || '',
    user_ids: oldData.user_ids || [],
    campus_id: oldData.campus_id || session.campusId,
  }];

  return apiTransaction({
    transType: cloudType('group_edit'),
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Delete one or more user groups.
 *
 * V1 TransType: group_delete / group_delete2
 *
 * @param {Array<object>} groups — array of { group_id, user_ids, cognito_ids }
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function deleteUserGroups(groups, session) {
  const payload = groups.map((g) => ({
    group_id: g.group_id,
    user_ids: g.user_ids || [],
    cognito_ids: g.cognito_ids || [],
  }));

  return apiTransaction({
    transType: cloudType('group_delete'),
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}
